import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "@/ui/Inputs/SystemValidationTextField";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { AnnieFeedbackReasonSelector } from "./AnnieFeedbackReasonSelector";
import type { AnnieFeedbackEditorController } from "./useAnnieFeedbackEditor";

const COMMENT_LIMIT = 1_000;

type Props = { conversationId: number; editor: AnnieFeedbackEditorController };

export const AnnieFeedbackDetailsEditor: FC<Props> = ({ conversationId, editor }) => {
	const { t } = useTranslation();
	return (
		<Box data-testid={`annie-feedback-editor-${conversationId}`} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, mt: 1 }}>
			<Typography variant="body2">{t("annie.feedback.reason.prompt")}</Typography>
			<AnnieFeedbackReasonSelector
				conversationId={conversationId}
				value={editor.draftReason}
				disabled={editor.isSavingDetails}
				onChange={editor.setDraftReason}
			/>
			<SystemValidationTextField
				fullWidth
				value={editor.draftComment}
				disabled={editor.isSavingDetails}
				label={t("annie.feedback.comment.label")}
				placeholder={t("annie.feedback.comment.placeholder")}
				onChange={(event) => editor.setDraftComment(event.target.value)}
				reserveHeightForHelperText={false}
				dataTestId={`annie-feedback-comment-input-${conversationId}`}
				injectedProps={{ multiline: true, minRows: 2, slotProps: { htmlInput: { maxLength: COMMENT_LIMIT } } }}
			/>
			<Typography variant="caption" color="text.secondary">
				{editor.draftComment.length}/{COMMENT_LIMIT}
			</Typography>
			<Box sx={{ display: "flex", gap: 1 }}>
				<SystemButton
					buttonVariant="primary"
					size="small"
					touchTarget
					loading={editor.isSavingDetails}
					disabled={editor.isSavingDetails}
					onClick={() => void editor.saveDetails()}
					data-testid={`annie-feedback-save-button-${conversationId}`}
				>
					{t("annie.feedback.details.save")}
				</SystemButton>
				<SystemButton
					buttonVariant="borderless"
					size="small"
					touchTarget
					disabled={editor.isSavingDetails}
					onClick={editor.cancel}
					data-testid={`annie-feedback-cancel-button-${conversationId}`}
				>
					{t("annie.feedback.details.cancel")}
				</SystemButton>
			</Box>
		</Box>
	);
};
